import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Grid from "@mui/material/Grid"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import DataTable from "components/DataTable"
import StatusBadge from "./components/StatusBadge"
import SunPaymentBadge from "./components/SunPaymentBadge"
import ProcurementFilterBar from "./components/ProcurementFilterBar"
import ScopedDataTable from "./components/ScopedDataTable"
import useProcurementFilters from "./hooks/useProcurementFilters"
import { useGetProcurementRequestsQuery } from "shared/redux/endpoints/procurement"
import {
  selectListFilters,
  setListFilters,
  resetListFilters,
} from "shared/redux/slices/procurementSlice"
import { PR_STATUS_LABEL } from "./status"
import { REGION_OPTIONS, formatAmount } from "./constants"

const STATUS_OPTIONS = Object.entries(PR_STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}))

const MANAGER_FILTER_DEFAULTS = {
  search: "",
  region: "",
  center: "",
  category: "",
  department: "",
  status: "",
  from: "",
  to: "",
  awaitingMe: "",
}

// Organisation-wide PR list for ROLE_PROCUREMENT_MANAGER: full filter-bar,
// extended columns and an "Awaiting my approval" quick filter. Uses the
// existing PR data layer.
const ManagerPrView = () => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters(
    MANAGER_FILTER_DEFAULTS
  )
  const { data, isFetching } = useGetProcurementRequestsQuery({
    ...filters,
    scope: "all",
  })
  const awaiting = filters.awaitingMe === "1"

  const columns = useMemo(
    () => [
      { Header: "PR Code", accessor: "prCode" },
      { Header: "Created", accessor: "createdAt" },
      { Header: "Region", accessor: "region" },
      { Header: "Center", accessor: "centerName" },
      { Header: "Department", accessor: "departmentName" },
      { Header: "Category", accessor: "categoryName" },
      { Header: "Supplier", accessor: "supplierName" },
      {
        Header: "Total",
        accessor: "totalAmount",
        Cell: ({ value }) => formatAmount(value),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
      {
        Header: "Paid Status",
        accessor: "sunPaymentStatus",
        Cell: ({ value }) => <SunPaymentBadge status={value} />,
      },
      { Header: "Payment Timeline", accessor: "paymentTimeline" },
      {
        Header: "",
        accessor: "id",
        Cell: ({ value }) => (
          <MDButton
            size="small"
            variant="text"
            color="info"
            onClick={() => navigate(`/procurement/requests/${value}`)}
          >
            View
          </MDButton>
        ),
      },
    ],
    [navigate]
  )

  return (
    <MDBox p={3}>
      <MDBox mb={2}>
        <MDTypography variant="h4">PRs</MDTypography>
        <MDTypography variant="caption" color="text">
          Procurement › Purchase Requisitions
        </MDTypography>
      </MDBox>

      <ProcurementFilterBar
        fields={[
          "search",
          "region",
          "center",
          "category",
          "department",
          "status",
          "period",
        ]}
        statusOptions={STATUS_OPTIONS}
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
        extra={
          <MDButton
            variant={awaiting ? "gradient" : "outlined"}
            color="warning"
            onClick={() => setFilters({ awaitingMe: awaiting ? "" : "1" })}
          >
            Awaiting my approval
          </MDButton>
        }
      />

      <MDBox bgColor="white" borderRadius="lg" shadow="sm" p={1}>
        <ScopedDataTable
          columns={columns}
          rows={data?.items}
          isLoading={isFetching}
          emptyText="No purchase requisitions found."
        />
      </MDBox>
    </MDBox>
  )
}

// Existing requester/default PR list. Behaviour unchanged.
const DefaultPrView = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const filters = useSelector(selectListFilters)

  const { data, isFetching } = useGetProcurementRequestsQuery(filters)

  const handleFilter = (patch) => dispatch(setListFilters({ ...patch, page: 0 }))
  const handlePage = (page) => dispatch(setListFilters({ page }))

  const dataTable = useMemo(() => {
    const items = data?.items || []
    return {
      columns: [
        { Header: "PR Code", accessor: "prCode" },
        { Header: "Created", accessor: "createdAt" },
        { Header: "Region", accessor: "region" },
        { Header: "Department", accessor: "departmentName" },
        { Header: "Category", accessor: "categoryName" },
        { Header: "Total", accessor: "totalAmount" },
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
          Header: "Payment",
          accessor: "sunPaymentStatus",
          Cell: ({ value }) => <SunPaymentBadge status={value} />,
        },
        {
          Header: "",
          accessor: "id",
          Cell: ({ value }) => (
            <MDButton
              size="small"
              variant="text"
              color="info"
              onClick={() => navigate(`/procurement/requests/${value}`)}
            >
              View
            </MDButton>
          ),
        },
      ],
      rows: items,
    }
  }, [data, navigate])

  return (
    <MDBox p={3}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <MDBox>
          <MDTypography variant="h4">Purchase Requisition</MDTypography>
          <MDTypography variant="caption" color="text">
            Procurement › Purchase Requisition
          </MDTypography>
        </MDBox>
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => navigate("/procurement/requests/new")}
        >
          + New request
        </MDButton>
      </MDBox>

      <MDBox mb={2} p={2} bgColor="white" borderRadius="lg" shadow="sm">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <MDInput
              fullWidth
              labelType="non-sticky"
              labelText="PR Code"
              value={filters.prCode}
              onChange={(e) => handleFilter({ prCode: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText="Status"
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === filters.status) || null}
              onChange={(o) => handleFilter({ status: o?.value || "" })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText="Region"
              options={REGION_OPTIONS}
              value={REGION_OPTIONS.find((o) => o.value === filters.region) || null}
              onChange={(o) => handleFilter({ region: o?.value || "" })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => dispatch(resetListFilters())}
            >
              Reset filters
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox bgColor="white" borderRadius="lg" shadow="sm" p={1}>
        {isFetching ? (
          <MDTypography variant="body2" color="text" p={2}>
            Loading…
          </MDTypography>
        ) : (
          <DataTable table={dataTable} noEndBorder entriesPerPage={{ isShow: false }} />
        )}
      </MDBox>

      {data?.total > 0 && (
        <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="caption" color="text">
            {data.items?.length || 0} of {data.total} entries
          </MDTypography>
          <MDBox display="flex" gap={1}>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              disabled={filters.page <= 0}
              onClick={() => handlePage(filters.page - 1)}
            >
              Prev
            </MDButton>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              disabled={(filters.page + 1) * filters.size >= data.total}
              onClick={() => handlePage(filters.page + 1)}
            >
              Next
            </MDButton>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  )
}

const PurchaseRequisitionContainer = ({ manager = false }) => (
  <DashboardLayout>
    {manager ? <ManagerPrView /> : <DefaultPrView />}
  </DashboardLayout>
)

export default PurchaseRequisitionContainer
